import { Fragment, useContext, useEffect, useMemo, useRef, useState } from "react";
import { GlobalContext } from "../../Context/GlobalContext";
import { useInfiniteHits, useInstantSearch, useStats } from "react-instantsearch";
import { isValidArray } from "../../Layout/Common/common";
import { getSearchFamiliesByLowestPrice } from "../../Layout/Common/algoliaInstantLib";
import { ProductCardAlgoliaList } from "../../Layout/Common/productCardAlgoliaList";
import { ProductCardAlgolia } from "../../Layout/Common/productCardAlgolia";
import { LazyCard } from "../../Layout/Common/lazyCard";
import { useAuth } from "../../Context/AuthContext";
import { DynamicLoader } from "./dynamicLoader";
import { ProductSkeletonLoader } from "./productSkeletonLoader";
import { NoResultFound } from "./noResultFound";
import { useGridScrollRestore } from "../../Context/hook/useGridScrollRestore";
import { primeProductPreview } from "../ProductHybrid/productPreviewCache";

export const SearchPageResultItems = () => {
  const { isGridView } = useContext(GlobalContext);
  const { priceCode } = useAuth();
  const { status } = useInstantSearch();
  const { nbHits } = useStats();
  const { currentPageHits, showMore, results, isLastPage } = useInfiniteHits();
  const [isPaginating, setIsPaginating] = useState(false);
  const [pagedHits, setPagedHits] = useState([]);
  const [renderedPages, setRenderedPages] = useState(0);
  const loadedPagesRef = useRef(new Set());
  const hasRenderedHits = useRef(false);

  const page = results?.page ?? 0;

  useEffect(() => {
    if (!isValidArray(currentPageHits)) return;
    if (page === 0) {
      loadedPagesRef.current = new Set([0]);
      setPagedHits([currentPageHits]);
      return;
    }
    if (loadedPagesRef.current.has(page)) return;
    loadedPagesRef.current.add(page);
    setPagedHits((prev) => [...prev, currentPageHits]);
  }, [currentPageHits, page]);

  const itemList = useMemo(() => {
    const shownFamilies = new Set();
    return pagedHits.flatMap((hits) =>
      getSearchFamiliesByLowestPrice(hits, priceCode).filter((item) => {
        if (shownFamilies.has(item.key)) return false;
        shownFamilies.add(item.key);
        return true;
      }),
    );
  }, [pagedHits, priceCode]);

  useEffect(() => {
    itemList.forEach(primeProductPreview);
    setRenderedPages(pagedHits.length);
  }, [itemList, pagedHits.length]);

  useEffect(() => {
    if (isValidArray(pagedHits)) hasRenderedHits.current = true;
  }, [pagedHits]);

  useEffect(() => {
    if (status === "idle") setIsPaginating(false);
  }, [status, page]);

  const waitingForRender = isPaginating || status !== "idle" || renderedPages !== pagedHits.length;

  const loadNextPage = () => {
    setIsPaginating(true);
    showMore();
  };

  const handleShowMore = () => {
    if (waitingForRender) return;
    loadNextPage();
  };
  const { isRestoring, onCardClickCapture } = useGridScrollRestore({
    page,
    isLastPage,
    status,
    showMore: loadNextPage,
    hasHits: isValidArray(itemList),
  });

  if (status !== "idle" && !hasRenderedHits.current) return <ProductSkeletonLoader />;
  if (nbHits === 0 && status === "idle") return <NoResultFound />;
  if (!isValidArray(itemList)) return null;

  const cardKey = (hit, index) => hit.key ?? `item-${index}`;

  const showMoreLoader = isLastPage === false && (
    <DynamicLoader
      isGridView={isGridView}
      isLastPage={isLastPage}
      showMore={handleShowMore}
      currentPage={page}
      paused={isRestoring || waitingForRender}
    />
  );

  if (isGridView) {
    return (
      <div className="col-lg-12" style={{ opacity: status !== "idle" && !isPaginating ? 0.5 : 1 }}>
        <div className="productCard-wrapper">
          {itemList.map((hit, index) => (
            <LazyCard
              data-view={isGridView}
              className="productCard"
              id={`product-${cardKey(hit, index)}`}
              data-insights-object-id={hit.productId}
              key={cardKey(hit, index)}
              onClickCapture={onCardClickCapture}
            >
              <ProductCardAlgolia data={hit} catFilter={false} />
            </LazyCard>
          ))}
        </div>
        {showMoreLoader}
      </div>
    );
  }

  return (
    <Fragment>
      {itemList.map((hit, index) => (
        <LazyCard
          data-view={isGridView}
          className="search-row rowProducts-list position-relative"
          id={`product-${cardKey(hit, index)}`}
          data-insights-object-id={hit.productId}
          key={cardKey(hit, index)}
          onClickCapture={onCardClickCapture}
        >
          <ProductCardAlgoliaList data={hit} index={hit.productId} />
        </LazyCard>
      ))}
      {showMoreLoader}
    </Fragment>
  );
};
